import React from 'react';
import { observer } from 'mobx-react';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useTheme } from 'styled-components';
import { Flex } from '@ui-library/flex';
import { BaseText, H4, LargeText } from '@ui-library/typography';
import { Button } from '@ui-library/button';
import { XMark } from '@ui-library/icons';
import { getDynamicTestId } from '@lib/utils/testId';

import { useMultiStepModalStore } from './modalStore';
import {
  CrossCloseButtonProps,
  ModalBreadcrumbProps,
  ModalButtonProps,
  ModalFooterProps,
  ModalHeaderProps,
} from './types';

export const CrossCloseButton: React.FC<CrossCloseButtonProps> = observer(({ disabled }) => {
  const store = useMultiStepModalStore();
  const isNext = store.config.variant === 'next';
  return (
    <Button
      ghost={!isNext}
      type="text"
      variant={isNext ? 'next' : undefined}
      level={isNext ? 'tertiary' : undefined}
      disabled={disabled || store.isLoading}
      style={
        isNext
          ? { position: 'absolute', top: 0, right: 0 }
          : { position: 'absolute', top: '-24px', right: '-24px' }
      }
      size="small"
      icon={<XMark />}
      onClick={() => store.dismiss()}
    />
  );
});

export const ModalBreadcrumb: React.FC<ModalBreadcrumbProps> = observer(
  ({ allowNavigation = true, showFullPath = true, nextPathKey, ...props }) => {
    const store = useMultiStepModalStore();
    const path = showFullPath ? store.getFullPath(nextPathKey) : store.path;
    const loading = store.isLoading;
    const { token } = useTheme();

    return (
      <Flex {...props} gap="10px" alignItemsCenter>
        {path.map((step, index) => {
          const canNavigate = allowNavigation && !step.isActive && !step.isNext && !loading;

          return (
            <>
              {step.icon && <FontAwesomeIcon icon={step.icon} fontSize={token.fontSize} />}
              <BaseText
                fontWeight={500}
                {...(canNavigate
                  ? { style: { cursor: 'pointer' }, onClick: () => store.navigate(step.key) }
                  : {})}
                color={step.isActive ? token.colorTextHeading : token.colorTextDisabled}
                key={step.key}
              >
                {step.title}
              </BaseText>
              {index !== path.length - 1 && (
                <FontAwesomeIcon
                  color={token.colorTextTertiary}
                  fontSize={token.fontSize}
                  icon={regular('angles-right')}
                />
              )}
            </>
          );
        })}
      </Flex>
    );
  },
);

const ModalButton: React.FC<ModalButtonProps> = ({ text, loadingText, ...props }) => (
  <Button data-testid={getDynamicTestId(text, 'btn')} {...props}>
    {props.loading ? loadingText || text : text}
  </Button>
);

export const MultiStepModalFooter: React.FC<ModalFooterProps> = observer(
  ({ dismissButton, actionButton, backButton, nextStepKey, disabled, loading }) => {
    const controlledNextStepKey = typeof nextStepKey !== 'undefined';
    const store = useMultiStepModalStore();
    const isNext = store.config.variant === 'next';

    const handleAction = () => {
      store.next(nextStepKey);
    };

    const handlePrev = () => {
      store.prev();
    };

    const handleDismiss = () => {
      store.dismiss();
    };

    const isLoading = store.isLoading || loading;

    const backButtonProps = isNext
      ? ({ variant: 'next', level: 'tertiary' } as const)
      : ({ type: 'text' } as const);

    const dismissButtonProps = isNext
      ? ({ variant: 'next', level: 'secondary' } as const)
      : ({ type: 'default' } as const);

    const actionButtonProps = isNext ? ({ variant: 'next', level: 'primary' } as const) : {};

    return (
      <Flex justifyContent="space-between" alignItemsCenter>
        {(backButton?.visible === true || (store.hasPrev && backButton?.visible !== false)) && (
          <ModalButton
            disabled={isLoading}
            text="Back"
            onClick={handlePrev}
            {...backButtonProps}
            {...backButton}
          />
        )}
        <Flex gap="8px" className="m-l-auto" alignItemsCenter>
          {dismissButton?.visible !== false && (
            <ModalButton
              text="Cancel"
              size="middle"
              disabled={isLoading}
              onClick={handleDismiss}
              {...dismissButtonProps}
              {...dismissButton}
            />
          )}
          {(actionButton?.visible === true ||
            (actionButton?.visible !== false && (store.isLinear || controlledNextStepKey))) && (
            <ModalButton
              text={store.hasNext ? 'Next' : 'Done'}
              size="middle"
              disabled={disabled || store.isDisabled}
              loading={isLoading}
              onClick={handleAction}
              {...actionButtonProps}
              {...actionButton}
            />
          )}
        </Flex>
      </Flex>
    );
  },
);

export const ModalHeader: React.FC<ModalHeaderProps> = observer(
  ({ title, description, icon, ...props }) => {
    const store = useMultiStepModalStore();
    const isNext = store.config.variant === 'next';
    const { token } = useTheme();

    return (
      <Flex justifyContent="space-between" {...props}>
        <Flex flexDirection="column" gap="4px">
          <Flex gap="8px" className="p-r-md">
            {icon && (
              <FontAwesomeIcon
                icon={icon.icon}
                color={icon.color || token.colorPrimary}
                fontSize="24px"
              />
            )}
            {isNext ? <LargeText strong>{title}</LargeText> : <H4>{title}</H4>}
          </Flex>
          {description && (
            <BaseText fontWeight={500} color={token.colorTextDescription}>
              {description}
            </BaseText>
          )}
        </Flex>
      </Flex>
    );
  },
);
